import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  schema?: object;
  ogImage?: string;
  ogType?: string;
  noindex?: boolean;
  nofollow?: boolean;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonical,
  schema,
  ogImage = 'https://static.readdy.ai/image/9e62058216dff63d1a686833c97014ce/727bcd7add4cd840d4bb2a3f870f0b81.png',
  ogType = 'website',
  noindex = false,
  nofollow = false,
}: SEOHeadProps) {
  useEffect(() => {
    // Set title
    document.title = title;

    // Helper function to set or update meta tags
    const setMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Set link tags
    const setLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      
      element.href = href;
    };

    // Basic meta tags
    setMetaTag('description', description);
    setMetaTag('keywords', keywords);
    
    // Robots meta
    const robotsContent = [];
    if (noindex) robotsContent.push('noindex');
    else robotsContent.push('index');
    if (nofollow) robotsContent.push('nofollow');
    else robotsContent.push('follow');
    robotsContent.push('max-image-preview:large');
    robotsContent.push('max-snippet:-1');
    robotsContent.push('max-video-preview:-1');
    setMetaTag('robots', robotsContent.join(', '));
    setMetaTag('googlebot', robotsContent.join(', '));
    setMetaTag('bingbot', robotsContent.join(', '));

    // Open Graph tags
    setMetaTag('og:title', title, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:url', canonical, true);
    setMetaTag('og:type', ogType, true);
    setMetaTag('og:image', ogImage, true);
    setMetaTag('og:image:width', '1200', true);
    setMetaTag('og:image:height', '630', true);
    setMetaTag('og:image:alt', title, true);
    setMetaTag('og:locale', 'nl_BE', true);
    setMetaTag('og:site_name', 'DSF Cartech', true);

    // Twitter Card tags
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', title);
    setMetaTag('twitter:description', description);
    setMetaTag('twitter:image', ogImage);
    setMetaTag('twitter:image:alt', title);

    // Additional SEO tags
    setMetaTag('format-detection', 'telephone=yes');
    setMetaTag('theme-color', '#000000');
    setMetaTag('apple-mobile-web-app-capable', 'yes');
    setMetaTag('apple-mobile-web-app-status-bar-style', 'black');
    setMetaTag('apple-mobile-web-app-title', 'DSF Cartech');

    // Canonical URL
    setLinkTag('canonical', canonical);

    // Last modified date
    const lastModified = new Date().toISOString();
    setMetaTag('last-modified', lastModified);
    setMetaTag('article:modified_time', lastModified, true);

    // Schema.org JSON-LD
    if (schema) {
      let scriptElement = document.querySelector('script[type="application/ld+json"]');
      
      if (!scriptElement) {
        scriptElement = document.createElement('script');
        scriptElement.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptElement);
      }
      
      scriptElement.textContent = JSON.stringify(schema);
    }

    // Cleanup function
    return () => {
      // Keep meta tags for SPA navigation
    };
  }, [title, description, keywords, canonical, schema, ogImage, ogType, noindex, nofollow]);

  return null;
}
